import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const TransactionTable = ({ 
  transactions = [], 
  showFilters = false, 
  filters = {}, 
  onFilterChange, 
  onRefresh,
  compact = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localFilters, setLocalFilters] = useState(filters);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'processing':
        return 'bg-primary/10 text-primary';
      case 'failed': case'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'earning':
        return 'TrendingUp';
      case 'withdrawal':
        return 'ArrowDownCircle';
      case 'bonus':
        return 'Gift';
      case 'refund':
        return 'RotateCcw';
      case 'penalty':
        return 'AlertTriangle';
      default:
        return 'DollarSign';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'earning': case'bonus': case'refund':
        return 'text-success';
      case 'withdrawal': case'penalty':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const handleFilterApply = () => {
    if (onFilterChange) {
      onFilterChange(localFilters);
    }
  };

  const handleFilterReset = () => {
    const resetFilters = { type: '', status: '', startDate: '', endDate: '' };
    setLocalFilters(resetFilters);
    if (onFilterChange) {
      onFilterChange(resetFilters);
    }
  };

  const filteredTransactions = transactions?.filter(transaction => {
    if (!searchTerm) return true;
    const search = searchTerm?.toLowerCase();
    return (
      transaction?.description?.toLowerCase()?.includes(search) ||
      transaction?.referenceNumber?.toLowerCase()?.includes(search) ||
      transaction?.job?.title?.toLowerCase()?.includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-PH', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
            Transaction History
          </h2>
          <p className="text-sm text-muted-foreground">
            {filteredTransactions?.length || 0} transaction{filteredTransactions?.length !== 1 ? 's' : ''}
          </p>
        </div>
        {onRefresh && (
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            onClick={onRefresh}
          >
            Refresh
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              label="Type"
              value={localFilters?.type || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, type: e?.target?.value })}
            >
              <option value="">All Types</option>
              <option value="earning">Earning</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="bonus">Bonus</option>
              <option value="refund">Refund</option>
              <option value="penalty">Penalty</option>
            </Select>

            <Select
              label="Status"
              value={localFilters?.status || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, status: e?.target?.value })}
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </Select>

            <Input
              type="date"
              label="Start Date"
              value={localFilters?.startDate || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, startDate: e?.target?.value })}
            />

            <Input
              type="date"
              label="End Date"
              value={localFilters?.endDate || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, endDate: e?.target?.value })}
            />
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={handleFilterApply}>
              Apply Filters
            </Button>
            <Button size="sm" variant="outline" onClick={handleFilterReset}>
              Reset
            </Button>
          </div>
        </div>
      )}

      <div className="mb-4">
        <Input
          type="text"
          placeholder="Search by description, reference, or job title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
      </div>

      {compact ? (
        <div className="space-y-3">
          {filteredTransactions?.slice(0, 10)?.map((transaction) => (
            <div
              key={transaction?.id}
              className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg bg-card flex items-center justify-center ${getTypeColor(transaction?.type)}`}>
                  <Icon name={getTypeIcon(transaction?.type)} size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{transaction?.description}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(transaction?.createdAt)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${transaction?.type === 'earning' || transaction?.type === 'bonus' ? 'text-success' : 'text-destructive'}`}>
                  {transaction?.type === 'earning' || transaction?.type === 'bonus' ? '+' : '-'}₱{parseFloat(transaction?.amount || 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(transaction?.status)}`}>
                  {transaction?.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Description</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Reference</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-foreground">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions?.map((transaction) => (
                <tr key={transaction?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {formatDate(transaction?.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name={getTypeIcon(transaction?.type)} size={16} className={getTypeColor(transaction?.type)} />
                      <span className="text-sm capitalize">{transaction?.type}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {transaction?.description}
                    {transaction?.job?.title && (
                      <p className="text-xs text-muted-foreground mt-0.5">{transaction?.job?.title}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                    {transaction?.referenceNumber || 'N/A'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(transaction?.status)}`}>
                      {transaction?.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-semibold ${transaction?.type === 'earning' || transaction?.type === 'bonus' ? 'text-success' : 'text-destructive'}`}>
                      {transaction?.type === 'earning' || transaction?.type === 'bonus' ? '+' : '-'}₱{parseFloat(transaction?.amount || 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredTransactions?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No transactions found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionTable;